import { BrowserRouter, Route, Routes, useNavigate } from "react-router-dom";
// @ts-ignore
import "@fontsource/roboto/300.css";
// @ts-ignore
import "@fontsource/roboto/400.css";
// @ts-ignore
import "@fontsource/roboto/500.css";
// @ts-ignore
import "@fontsource/roboto/700.css";
import HomePage from "./components/pages/Home";
import {
  Backdrop,
  Box,
  CircularProgress,
  CssBaseline,
  ThemeProvider,
} from "@mui/material";
import { useEffect, useMemo } from "react";
import { useGlobalContext } from "./stores/Global";
import LoginPage from "./components/pages/Login";
import { ToastContainer } from "react-toastify";
// @ts-ignore
import "react-toastify/dist/ReactToastify.css";
import RegisterPage from "./components/pages/Register";
import {
  AuthService,
  BrandingService,
  NotificationService,
  SettingsService,
} from "./services/api";
import MyTicketsPage from "./components/pages/MyTickets";
import CreateMyTicketPage from "./components/pages/Tickets/CreateMyTicket";
import ManageTicketPage from "./components/pages/Tickets/ManageTicket";
import MainTemplate from "./components/templates/MainTemplate";
import { startServiceWorker, stopServiceWorker } from "./workers/main";
import SetupPage from "./components/pages/Setup";
import ForgotPasswordPage from "./components/pages/ForgotPassword";
import { generateTheme } from "./themes/akcaba";
import AccountInfoPage from "./components/pages/AccountInfo";
import useBreakpoint from "./hooks/useBreakpoint";
import "./config/i18n";
import ManagementTemplate from "./components/templates/ManagementTemplate";
import { useInterval } from "./hooks/useInterval";
import ConfirmDialog from "./components/molecules/ConfirmDialog";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useBrandingStore } from "./stores/Branding";

function IndexRoute() {
  const navigate = useNavigate();
  const checkSetup = async () => {
    const res = await SettingsService.getSetup();
    if (!res) return navigate("/home");
    const setup = res.data.data;
    if (!setup) navigate("/setup");
    else navigate("/home");
  };

  useEffect(() => {
    checkSetup();
  }, []);
  return null;
}

export default function App() {
  const { context, setContext } = useGlobalContext();
  const breakpoint = useBreakpoint();
  const setBranding = useBrandingStore((s) => s.setBranding);
  const resetBranding = useBrandingStore((s) => s.resetBranding);
  const themeColor = useBrandingStore((s) => s.branding.themeColor);
  // useSocket(true);

  const theme = useMemo(() => {
    return generateTheme(themeColor);
  }, [themeColor]);

  useInterval(() => {
    if (context.user?._id) NotificationService.updateSubscriptionActivity();
  }, 30 * 1000);

  const { mutate: loadUser, isPending } = useMutation({
    mutationFn: async () => {
      const language = localStorage.getItem("language");
      if (!localStorage.getItem("authToken"))
        setContext({
          user: undefined,
          language: language || context.language,
        });
      const res = await AuthService.getSession();
      return res?.data;
    },
    onSettled: (data) => {
      const language = localStorage.getItem("language");
      // Mark the initial session check as done so route guards (e.g.
      // management pages) can safely react to a missing user.
      setContext({ sessionChecked: true });
      if (data?.success) {
        setContext({
          user: data.data?.user,
          language: language || context.language,
        });
        if (data.data?.newToken)
          localStorage.setItem("authToken", data.data?.newToken);
        NotificationService.updateSubscriptionActivity();
        startServiceWorker();
      } else {
        stopServiceWorker();
      }
    },
  });

  const { mutate: loadBranding } = useMutation({
    mutationFn: async () => {
      const res = await BrandingService.getBranding();
      return res?.data?.data;
    },
    onSuccess: (data) => {
      if (data) setBranding(data);
      else if (data === null) resetBranding();
    },
  });

  useEffect(() => {
    loadUser();
  }, []);

  useQuery({
    queryKey: ["branding"],
    queryFn: async () => {
      loadBranding();
      return true;
    },
    refetchInterval: 60 * 1000,
  });

  return (
    <Box height={1} width={1}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ConfirmDialog />
        <ToastContainer
          position={breakpoint.md ? "top-center" : "bottom-center"}
          style={{ zIndex: 999999999 }}
          toastStyle={{
            whiteSpace: "pre-line",
          }}
        />
        <Backdrop
          open={isPending}
          sx={{ zIndex: 999999999, color: "background.default" }}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
        <BrowserRouter>
          <Routes>
            <Route index element={<IndexRoute />} />
            <Route path="/setup" element={<SetupPage />} />
            <Route path="/home" element={<HomePage />} />
            <Route path="/my-tickets">
              <Route index element={<MyTicketsPage />} />
              <Route path="create" element={<CreateMyTicketPage />} />
              <Route
                path=":_id"
                element={
                  <MainTemplate>
                    <ManageTicketPage />
                  </MainTemplate>
                }
              />
            </Route>
            <Route path="/account">
              <Route index element={<ForgotPasswordPage />} />
              <Route path="info" element={<AccountInfoPage />} />
              <Route path="forgot-password" element={<ForgotPasswordPage />} />
            </Route>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/m/*" element={<ManagementTemplate />} />
          </Routes>
        </BrowserRouter>
      </ThemeProvider>
    </Box>
  );
}
